import React, { useContext, useState, useEffect } from 'react';
import Api from 'api';
import withForm from 'components/Forms/withForm';
import {
  FormGrid,
  FormField,
  FormRow,
  FormColumn,
  FormSubmit,
  FormCheckbox,
  FormFieldUpload,
  MandatoryFootnote,
} from 'components/Forms';
import { corporateFormSchema } from 'components/Forms/validationSchemas';
import { observer, storage } from 'store';
import {
  ErrorsShakeHubContext,
  findFirstFieldWithError,
  getInitialPhoneValue,
} from 'components/Forms/utils';
import Input from 'components/UI/Input';
import { useTrans } from 'utils/localisation';
import { pushCustomSubmitEvent } from 'utils/analytics';
import { appendScriptSrc } from 'utils/scripts';
import { recaptchSrc } from 'define/scripts';

import * as styles from './CorporateForm.module.scss';

const Form = ({
  id,
  formName,
  className,
  submitTitle,
  displaySubmitButton,
  handleSubmit,
  fields,
  isValid,
  values,
  errors,
  isSubmitting,
  status,
  setStatus,
  withAgreement,
  withAttach,
}) => {
  const t = useTrans(['corporateForm', 'common', 'nda', 'validation']);
  const errorsShakeHub = useContext(ErrorsShakeHubContext);
  const [btnPressed, setBtnPressed] = useState(false);

  useEffect(() => {
    const scriptLoaded = document.getElementById('recaptcha');

    if (!scriptLoaded) {
      appendScriptSrc(recaptchSrc, 'body', 'recaptcha');
    }

    return () => storage.addItem('isEmailCheckedSuccessful', false);
  }, []);

  useEffect(() => {
    if (!isValid && btnPressed) {
      errorsShakeHub.dispatchEvent(new CustomEvent('shakeError'));
      errorsShakeHub.dispatchEvent(
        new CustomEvent('setFocus', {
          detail: findFirstFieldWithError(values, errors),
        }),
      );
      setBtnPressed(false);
    }
  }, [isValid, btnPressed]);

  useEffect(() => {
    if (status === 'success') {
      setStatus();
    }
  }, [status]);

  return (
    <FormGrid
      submitHandler={e => {
        setBtnPressed(true);
        handleSubmit(e);
      }}
      formName={formName}
      id={id}
      className={className}
    >
      <MandatoryFootnote />
      <FormRow sizes={['md12']}>
        <FormColumn>
          <FormField Component={Input} {...fields.name} />
          <FormField
            Component={Input}
            {...fields.email}
            isLoading={storage.getItem('isEmailCheckLoading')}
            isChecked={storage.getItem('isEmailCheckedSuccessful')}
          />
        </FormColumn>
      </FormRow>
      <FormRow sizes={['md12']}>
        <FormColumn>
          <FormField Component={Input} {...fields.phone} />
        </FormColumn>
      </FormRow>
      <FormRow sizes={['md12']}>
        <FormColumn>
          <FormField Component={Input} {...fields.message} />
        </FormColumn>
      </FormRow>
      <FormRow sizes={['md12']}>
        <FormColumn>
          {withAttach && <FormFieldUpload {...fields.files} />}
          {withAgreement && (
            <FormField
              className={styles.agreement}
              Component={FormCheckbox}
              {...fields.agreement}
            />
          )}
        </FormColumn>
      </FormRow>
      <div className={styles.submitWrap}>
        <FormSubmit
          title={submitTitle || t('send')}
          displaySubmitButton={displaySubmitButton}
          nda={fields.nda}
          disabled={isSubmitting || (withAgreement && !values.agreement)}
        />
      </div>
    </FormGrid>
  );
};

export default function CorporateForm({
  id,
  formName,
  commentPlaceholder,
  commentPlaceholderMessage,
  closeModal,
  onSuccess,
  onResponse,
  additionalFormData,
  className,
  submitTitle,
  getAdditionalFormData,
  inModal = false,
  ndaPositionTop = false,
  withNda = true,
  withAgreement = false,
  withAttach = true,
  displaySubmitButton,
  theme,
}) {
  const t = useTrans(['corporateForm', 'common', 'nda', 'validation']);

  const fields = {
    name: {
      initialValue: '',
      domProps: {
        placeholder: t('namePlaceholder'),
        maxLength: 255,
      },
    },
    email: {
      initialValue: '',
      validationTooltipText: t('notCorporateEmail'),
      inModal,
      domProps: {
        placeholder: `* ${t('emailPlaceholder')}`,
        type: 'email',
        maxLength: 254,
      },
    },
    phone: {
      initialValue: '',
      getInitialValue: getInitialPhoneValue,
      tooltip: {
        className: 'phoneTooltip',
        text: t('phoneTooltip'),
      },
      mask: {
        mask: '+99999999999999999',
        maskChar: '',
        formatChars: {
          9: '[0-9]',
        },
      },
      domProps: {
        placeholder: `* ${t('phonePlaceholder')}`,
        type: 'tel',
      },
    },
    message: {
      initialValue: '',
      isTextarea: true,
      domProps: {
        maxLength: 1000,
        placeholder: `* ${commentPlaceholder || t('commentPlaceholder')}`,
      },
    },
    files: {
      initialValue: [],
      upload: true,
      domProps: {
        type: 'file',
        accept: '.doc,.docx,.pdf,.ppt,.pptx',
        multiple: true,
      },
      content: {
        attach: t('file').attach,
        limitation: t('file').limitation,
      },
    },
  };

  if (withNda) {
    fields.nda = {
      initialValue: false,
      top: ndaPositionTop,
      inModal,
      withoutError: true,
      content: t('ndaText'),
      prependedTooltip: t('ndaHint'),
    };
  }

  if (withAgreement) {
    fields.agreement = {
      initialValue: false,
      content: 'I agree to the processing of my data in order to receive a sample document.',
    };
  }

  const errorsMessages = {
    phone: t('phoneMessage'),
    email: {
      required: t('emailRequired'),
      invalid: t('emailInvalid'),
      notCorporateEmail: t('notCorporateEmail'),
    },
    comment: commentPlaceholderMessage || t('commentMessage'),
    files: {
      size: t('file').size,
      smallSize: t('file').smallSize,
      type: t('file').type,
    },
    emoji: t('noEmoji'),
  };

  const defaultOnSuccess = () => storage.addItem('isEmailCheckedSuccessful', false);

  const Component = withForm({
    fields,
    validation: () => corporateFormSchema(errorsMessages),
    submit: {
      id,
      formName,
      handler: Api.sendFormData.bind(Api),
      reset: true,
      successModal: 'SuccessModal',
      errorModal: 'ErrorModal',
      onSuccess: onSuccess ?? defaultOnSuccess,
      additionalFormData,
      getAdditionalFormData,
      pushSubmitEvent: () => {
        pushCustomSubmitEvent(id);
      },
      onResponse: () => {
        if (typeof onResponse === 'function') {
          onResponse();
        }
        if (inModal) {
          closeModal();
        }
      },
    },
    id,
    formName,
    className,
    submitTitle,
    displaySubmitButton,
    theme,
    withAgreement,
    withAttach,
  })(observer(Form));

  return <Component />;
}
